import React, { ReactNode, useRef, useEffect } from 'react'

import styled from 'styled-components'

interface CarouselProps {
  children: ReactNode
  gap?: number
  padding?: number
  showScrollbar?: boolean
  infinite?: boolean
  /** Intervalo em ms para avançar automaticamente (ex: 2000 = 2 segundos). Quando definido, habilita autoPlay */
  timer?: number
}

// Easing suave: ease-in-out cúbico
const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)

const smoothScrollTo = (element: HTMLElement, targetLeft: number, duration = 750) => {
  const startLeft = element.scrollLeft
  const distance = targetLeft - startLeft
  const startTime = performance.now()

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    const eased = easeInOutCubic(progress)
    element.scrollLeft = startLeft + distance * eased
    if (progress < 1) requestAnimationFrame(animate)
  }

  requestAnimationFrame(animate)
}

const CarouselContainer = styled.div<{
  gap: number
  padding: number
  showScrollbar: boolean
}>`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior-x: contain;
  scrollbar-width: none;
  -ms-overflow-style: none;
  gap: ${(props) => props.gap}px;
  padding: 0;

  &::-webkit-scrollbar {
    display: none;
    width: 0;
    height: 0;
  }

  &::-webkit-scrollbar-track {
    display: none;
  }

  &::-webkit-scrollbar-thumb {
    display: none;
  }

  &::before,
  &::after {
    content: '';
    flex: 0 0 0px;
    background: transparent;
    transition: flex-basis 500ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  &:hover::before,
  &:hover::after {
    flex-basis: 2px;
  }
`

const CarouselItem = styled.div<{ padding: number }>`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  transition: transform 500ms cubic-bezier(0.23, 1, 0.32, 1);
  margin-left: ${(props) => props.padding}px;

  &:first-child {
    margin-left: 0;
  }

  &:active {
    transform: scale(0.98);
    transition: transform 150ms cubic-bezier(0.23, 1, 0.32, 1);
  }

  &:hover {
    transform: scale(1.02);
    transition: transform 350ms cubic-bezier(0.23, 1, 0.32, 1);
  }
`

const Carousel: React.FC<CarouselProps> = ({
  children,
  gap = 16,
  padding = 16,
  showScrollbar = false,
  infinite = false,
  timer,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const childrenArray = React.Children.toArray(children)
  const isJumpingRef = useRef(false)
  const scrollJumpedRef = useRef(false)
  const isUserInteractingRef = useRef(false)

  // Conteúdo para renderizar: triplica quando infinite para criar o loop A,B,C,A,B,C,A,B,C
  const contentToRender = infinite
    ? [...childrenArray, ...childrenArray, ...childrenArray]
    : childrenArray

  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    let isScrolling = false
    let startX = 0
    let currentX = 0

    const handleTouchStart = (e: TouchEvent) => {
      isUserInteractingRef.current = true
      isScrolling = true
      startX = e.touches[0].clientX
      currentX = startX
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isScrolling) return

      currentX = e.touches[0].clientX
      const deltaX = currentX - startX

      const { scrollLeft, scrollWidth, clientWidth } = container
      const maxScroll = scrollWidth - clientWidth

      if (scrollLeft <= 0 && deltaX > 0) {
        e.preventDefault()
        const resistance = Math.min(deltaX * 0.3, 50)
        container.style.transform = `translateX(${resistance}px)`
      } else if (scrollLeft >= maxScroll && deltaX < 0) {
        e.preventDefault()
        const resistance = Math.min(Math.abs(deltaX) * 0.3, 50)
        container.style.transform = `translateX(-${resistance}px)`
      }
    }

    const handleTouchEnd = () => {
      if (!isScrolling) return

      isScrolling = false
      isUserInteractingRef.current = false

      container.style.transition = 'transform 600ms cubic-bezier(0.23, 1, 0.32, 1)'
      container.style.transform = 'translateX(0)'

      setTimeout(() => {
        if (container) {
          container.style.transition = ''
        }
      }, 600)
    }

    let isDragging = false
    let dragStartX = 0
    let scrollLeftStart = 0

    const handleMouseDown = (e: MouseEvent) => {
      isUserInteractingRef.current = true
      isDragging = true
      dragStartX = e.clientX
      scrollLeftStart = container.scrollLeft
      container.style.cursor = 'grabbing'
      container.style.userSelect = 'none'
    }

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging) return
      e.preventDefault()
      // Após um jump do scroll infinito, atualiza o âncora do drag para não sobrescrever a posição
      if (scrollJumpedRef.current) {
        scrollLeftStart = container.scrollLeft
        dragStartX = e.clientX
        scrollJumpedRef.current = false
      }
      const delta = e.clientX - dragStartX
      container.scrollLeft = scrollLeftStart - delta
    }

    const handleMouseUp = () => {
      isDragging = false
      isUserInteractingRef.current = false
      container.style.cursor = 'grab'
      container.style.userSelect = ''
    }

    container.style.cursor = 'grab'

    container.addEventListener('touchstart', handleTouchStart, { passive: false })
    container.addEventListener('touchmove', handleTouchMove, { passive: false })
    container.addEventListener('touchend', handleTouchEnd)
    container.addEventListener('mousedown', handleMouseDown)
    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseup', handleMouseUp)
    container.addEventListener('mouseleave', handleMouseUp)

    return () => {
      container.removeEventListener('touchstart', handleTouchStart)
      container.removeEventListener('touchmove', handleTouchMove)
      container.removeEventListener('touchend', handleTouchEnd)
      container.removeEventListener('mousedown', handleMouseDown)
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseup', handleMouseUp)
      container.removeEventListener('mouseleave', handleMouseUp)
    }
  }, [])

  // Lógica de scroll infinito: quando chega nas bordas, pula para a posição equivalente no set do meio
  useEffect(() => {
    const container = containerRef.current
    if (!container || !infinite || childrenArray.length === 0) return undefined

    const handleScroll = () => {
      if (isJumpingRef.current) return

      const { scrollLeft, scrollWidth, clientWidth } = container
      const setWidth = scrollWidth / 3

      // Evita triggers incorretos quando o layout ainda não está pronto
      if (setWidth <= 0) return

      // Entrando no primeiro set (esquerda) -> pula para o set do meio
      if (scrollLeft <= 0) {
        isJumpingRef.current = true
        scrollJumpedRef.current = true
        const prevScrollBehavior = container.style.scrollBehavior
        container.style.scrollBehavior = 'auto' // Jump instantâneo, sem animação
        container.scrollLeft = scrollLeft + setWidth
        container.style.scrollBehavior = prevScrollBehavior
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            isJumpingRef.current = false
          })
        })
      }
      // Entrando no terceiro set (direita) -> pula para o set do meio
      else if (scrollLeft >= setWidth * 2 - clientWidth - 1) {
        isJumpingRef.current = true
        scrollJumpedRef.current = true
        const prevScrollBehavior = container.style.scrollBehavior
        container.style.scrollBehavior = 'auto' // Jump instantâneo, sem animação
        container.scrollLeft = scrollLeft - setWidth
        container.style.scrollBehavior = prevScrollBehavior
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            isJumpingRef.current = false
          })
        })
      }
    }

    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [infinite, childrenArray.length])

  // AutoPlay: avança um slide a cada timer ms (ex: A -> B -> C -> A)
  useEffect(() => {
    const container = containerRef.current
    if (!container || !timer || contentToRender.length === 0) return undefined

    const intervalId = setInterval(() => {
      if (isUserInteractingRef.current || isJumpingRef.current) return

      const { scrollLeft, scrollWidth, clientWidth } = container
      const slideWidth = scrollWidth / contentToRender.length

      if (slideWidth <= 0) return

      let targetLeft = scrollLeft + slideWidth

      // Com infinite e timer: ao cruzar para o 3º set, pula instantaneamente e anima só 1 slide
      if (infinite) {
        const setWidth = scrollWidth / 3
        const thirdSetStart = setWidth * 2 - clientWidth
        if (targetLeft >= thirdSetStart) {
          isJumpingRef.current = true
          const prevScrollBehavior = container.style.scrollBehavior
          container.style.scrollBehavior = 'auto'
          container.scrollLeft = scrollLeft - setWidth
          container.style.scrollBehavior = prevScrollBehavior
          targetLeft = container.scrollLeft + slideWidth
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              isJumpingRef.current = false
            })
          })
        }
      }

      smoothScrollTo(container, targetLeft)
    }, timer)

    return () => clearInterval(intervalId)
  }, [timer, contentToRender.length, infinite])

  return (
    <CarouselContainer ref={containerRef} gap={gap} padding={padding} showScrollbar={showScrollbar}>
      {contentToRender.map((child, index) => {
        const copyIndex = infinite ? Math.floor(index / childrenArray.length) : 0
        if (React.isValidElement(child)) {
          const childKey =
            child.key ||
            `carousel-item-${index % childrenArray.length}-${child.type?.toString() || 'unknown'}`
          return (
            <CarouselItem key={`${childKey}-copy-${copyIndex}`} padding={padding}>
              {child}
            </CarouselItem>
          )
        }
        const fallbackKey = `carousel-item-${
          index % childrenArray.length
        }-${typeof child}-copy-${copyIndex}`
        return (
          <CarouselItem key={fallbackKey} padding={padding}>
            {child}
          </CarouselItem>
        )
      })}
    </CarouselContainer>
  )
}

export default Carousel
